import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { format } from 'date-fns';
import { firestore } from '../../../../firebase';
import { useAuth } from '../../../../utils/Auth';
import styles from './ConnectButtonAnalytics.module.css';

const getButtonDisplayName = (analytics = {}, button = {}) => {
    const buttonName = typeof analytics?.buttonName === 'string' ? analytics.buttonName.trim() : '';
    const buttonText = typeof analytics?.buttonText === 'string' ? analytics.buttonText.trim() : '';
    const currentText = typeof button?.text === 'string' ? button.text.trim() : '';

    return buttonName || buttonText || currentText || 'Untitled Button';
};

const formatLastClickedAt = (value) => {
    if (!value) return 'No clicks yet';

    const dateValue = typeof value?.toDate === 'function' ? value.toDate() : value;
    const parsedDate = dateValue instanceof Date ? dateValue : new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
        return 'No clicks yet';
    }

    return format(parsedDate, 'MMM d, yyyy h:mm a');
};

const formatButtonTypeLabel = (value = '') => {
    const normalizedValue = String(value || '').trim();

    switch (normalizedValue.toLowerCase()) {
        case 'sms':
            return 'SMS';
        case 'linkedin':
            return 'LinkedIn';
        default:
            return normalizedValue;
    }
};

const getButtonClicks = (analytics = {}) => {
    return analytics?.scanCount_cache || 0;
};

export default function ConnectButtonAnalytics() {
    const { qr_data } = useAuth();
    const [analyticsDocs, setAnalyticsDocs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isExpanded, setIsExpanded] = useState(false);

    const qrId = qr_data?.id || '';
    const qrType = qr_data?.data?.qr_type || '';
    const buttons = useMemo(() => {
        const connectButtons = qr_data?.data?.connect?.buttons;
        return Array.isArray(connectButtons) ? connectButtons : [];
    }, [qr_data?.data?.connect?.buttons]);

    useEffect(() => {
        if (qrType !== 'connect' || !qrId) {
            setAnalyticsDocs([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const analyticsRef = collection(firestore, 'codes', qrId, 'connectButtonAnalytics');
        const unsubscribe = onSnapshot(
            analyticsRef,
            (snapshot) => {
                const nextDocs = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));

                setAnalyticsDocs(nextDocs);
                setIsLoading(false);
            },
            (error) => {
                console.error('Error loading connect button analytics:', error);
                setAnalyticsDocs([]);
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [qrId, qrType]);

    const mergedButtons = useMemo(() => {
        const analyticsById = new Map(
            analyticsDocs.map((doc) => [doc?.buttonId || doc?.id, doc])
        );

        return buttons.map((button) => {
            const analytics = analyticsById.get(button?.id) || null;
            const buttonType = analytics?.buttonType || button?.platform || button?.type || 'unknown';

            return {
                id: button?.id || analytics?.buttonId || analytics?.id || '',
                label: getButtonDisplayName(analytics, button),
                type: buttonType,
                platform: analytics?.platform || button?.platform || '',
                clicks: getButtonClicks(analytics),
                lastClickedAt: analytics?.lastClickedAt || null,
            };
        });
    }, [analyticsDocs, buttons]);

    if (qrType !== 'connect') {
        return null;
    }

    return (
        <div className={styles.container}>
            <button
                type='button'
                className={styles.headerToggle}
                onClick={() => setIsExpanded(prev => !prev)}
                aria-expanded={isExpanded}
            >
                <div className={styles.headerRow}>
                    <h5 className={styles.header}>Button Click Analytics</h5>
                    <div className={styles.headerActions}>
                        <span className={styles.meta}>
                            {buttons.length} button{buttons.length === 1 ? '' : 's'}
                        </span>
                        <span className={isExpanded ? styles.chevronExpanded : styles.chevronCollapsed}>
                            <i className='far fa-chevron-down' aria-hidden='true' />
                        </span>
                    </div>
                </div>
            </button>

            {isExpanded && (
                buttons.length === 0 ? (
                    <div className={styles.emptyState}>
                        No buttons have been configured for this QR Connect code yet.
                    </div>
                ) : isLoading ? (
                    <div className={styles.emptyState}>Loading button analytics...</div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Button</th>
                                    <th>Type</th>
                                    <th>Clicks</th>
                                    <th>Last Click</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mergedButtons.map((button) => (
                                    <tr key={button.id || button.label}>
                                        <td>
                                            <div className={styles.buttonCell}>
                                                <span className={styles.buttonName}>{button.label}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={styles.typePill}>
                                                {formatButtonTypeLabel(button.platform || button.type)}
                                            </span>
                                        </td>
                                        <td className={styles.clicksCell}>{button.clicks}</td>
                                        <td>{formatLastClickedAt(button.lastClickedAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}
        </div>
    );
}
